import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';

import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import '../../panels/emotes-panel';
import './game-chat';

/**
 * Chat Area - Layout container for the bottom-left chat stack
 *
 * Thin flex-column wrapper that owns positioning and width.
 * Children (the emotes panel and game-chat) stack vertically
 * and share the same width. Lifts itself to the panel tier of the
 * app z ladder while the emotes panel is open.
 *
 * @element chat-area
 */
@customElement('chat-area')
export class ChatArea extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      /* Only matters when the stack outgrows max-height (a tall chat with the
         radio panel open): the overflow spills off the top, so the newest chat
         line stays on screen instead of being pushed below it. */
      justify-content: flex-end;
      position: absolute;
      /* Vanilla hugs the screen edge at 10px; +4px of breathing room. The
         whole stack is bottom-anchored, so this lifts input + chatbox
         together without changing their heights. */
      bottom: 14px;
      left: 10px;
      width: 25%;
      min-width: 330px;
      /* Cap to the viewport, not just a fixed 800px. The resized width is
         persisted (displaySettings.chatWidth) and re-applied as an inline width
         on load, so a width saved on a wide monitor would otherwise overflow a
         narrower window. min() clamps the rendered width responsively (it
         re-evaluates on window resize) while the inline style keeps the user's
         preference, so the chat grows back toward 800px when the window widens. */
      max-width: min(800px, calc(100vw - 20px));
      max-height: calc(100vh - 24px);
      pointer-events: none;
      z-index: 5;
    }

    :host([data-panel-open]) {
      /* An open emotes panel is a panel like any other — same 300 tier as
         airmash-ui's .panel-overlay (see the z ladder there). It just happens
         to live here (anchored to chat) instead of in the overlay. At 25 it
         sat under game notifications and kill/death notices (z 50), which
         kept painting over the list. */
      z-index: 300;
    }
  `;

  private uiState = bindValtio(this, uiStateProxy);

  /**
   * Last reflected panel-open state. This component re-renders on any uiState
   * mutation (bindValtio is a coarse subscription) but only cares about
   * activePanel, so the memo skips the host-attribute write on every other tick.
   */
  private panelOpen = false;

  override connectedCallback() {
    super.connectedCallback();
    this.addEventListener('chat-resize-width', this.handleResizeWidth);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('chat-resize-width', this.handleResizeWidth);
  }

  private handleResizeWidth = (e: CustomEvent<{ width: number }>) => {
    this.style.width = `${e.detail.width}px`;
  };

  override updated() {
    const open = this.uiState.activePanel === 'emotes';
    if (open === this.panelOpen) return;
    this.panelOpen = open;
    this.toggleAttribute('data-panel-open', open);
  }

  override render() {
    return html`
      <emotes-panel></emotes-panel>
      <game-chat></game-chat>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chat-area': ChatArea;
  }
}
